import { createRouter, createWebHistory } from "vue-router";

function lazyLoad(view) {
  return () => import(`@/views/${view}.vue`);
}

const routes = [
  {
    path: "/",
    name: "home",
    component: lazyLoad("Home"),
    meta: { requiresAuth: true },
  },
  {
    path: "/about",
    name: "about",
    component: lazyLoad("About"),
    meta: { requiresAuth: true },
  },
  {
    // Die Hauptroute für Projekte
    path: "/projects",
    children: [
      {
        // Diese Route fängt sowohl '/projects' (alle Projekte)
        // als auch '/projects/:name' (gefilterte Projekte nach Kategorie) ab.
        // Der ':name?' macht den Parameter optional.
        path: ":name?", // Geändert: ':name' ist jetzt direkt unter /projects und optional
        name: "projects", // Behält den Namen 'projects' bei
        component: lazyLoad("Projects"), // Projects.vue wird für beide Fälle verwendet
        meta: { requiresAuth: true },
        props: true,
      },
      {
        // Dies ist für einzelne Projekte, z.B. /projects/mein-projekt-slug
        // Wichtig: Diese Route muss nach der optionalen Kategorie-Route definiert werden,
        // damit der Router zuerst versucht, einen Kategorie-Namen zu matchen.
        // Oder wir müssen eine Logik in Projects.vue implementieren, die zwischen
        // Kategorie-Name und Projekt-Slug unterscheidet.
        // Für eine eindeutige Unterscheidung ist es besser, den Slug explizit zu kennzeichnen
        // oder die Projekt-Slugs von den Kategorienamen zu unterscheiden.
        // Hier angenommen, dass Slugs und Kategorienamen sich nicht überlappen,
        // oder dass die Reihenfolge der Routen (längere/spezifischere zuerst) hilft.
        // Eine robustere Lösung wäre, Projekt-Slugs unter einem anderen Pfad zu haben,
        // z.B. /project-detail/:slug, oder eine Regex für :name.
        path: ":slug", // Behält den Slug für einzelne Projekte bei
        name: "project",
        component: lazyLoad("Project"),
        meta: { requiresAuth: true },
        props: true,
      },
    ],
  },
  {
    path: "/imprint",
    name: "imprint",
    component: lazyLoad("Imprint"),
    meta: { requiresAuth: true },
  },
  {
    path: "/data-protection",
    name: "data-protection",
    component: lazyLoad("DataProtection"),
    meta: { requiresAuth: false },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: lazyLoad("NotFound"),
    meta: { requiresAuth: false },
  },
  {
    path: "/login",
    name: "login",
    component: lazyLoad("Login"),
    meta: { requiresAuth: false },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { top: 0 };
  },
});

// Globale Navigation-Guard
router.beforeEach((to, from, next) => {
  const token = localStorage.getItem("token");
  const tokenExpire = localStorage.getItem("tokenExpire");
  const now = Date.now();
  const isAuthenticated = token && tokenExpire && now < parseInt(tokenExpire, 10);

  // Wenn die Zielroute Authentifizierung benötigt UND der Benutzer NICHT authentifiziert ist
  if (to.meta.requiresAuth && !isAuthenticated) {
    // Leite zur Login-Seite um
    next({
      name: "login",
      // Übernimm alle vorhandenen Query-Parameter von der ursprünglichen Ziel-URL
      // und füge den 'redirect'-Parameter hinzu.
      query: { ...to.query, redirect: to.fullPath },
    });
  } else if (to.name === "login" && isAuthenticated) {
    // Wenn der Benutzer bereits authentifiziert ist und versucht, die Login-Seite aufzurufen,
    // leite ihn zur Startseite oder zum Redirect-Ziel um.
    const redirectPath = to.query.redirect || '/';
    next(redirectPath);
  } else {
    // In allen anderen Fällen (keine Auth benötigt ODER Auth vorhanden), fahre fort
    next();
  }
});

export default router;
